import { faqsPl } from "@data/audyt-legacy-landing/faq";
import {
  aboutUsCopyPl,
  contactFormPl,
  faqStructuredData,
  maintenanceClusterLinks,
  maintenanceKnowledgeBaseLinks,
  navbarCopyPl,
  organizationStructuredData,
  serviceStructuredData,
} from "@data/maintenance-landing/shared";
import {
  AlertTriangle,
  Calendar,
  CheckCircle,
  Cloud,
  Code,
  FileSearch,
  FileText,
  Layers,
  LifeBuoy,
  Map,
  RefreshCw,
  Search,
  Server,
  Shield,
  Target,
  Users,
  Wrench,
} from "lucide-react";

import type { FAQCopy } from "@components/bluetooth-landing/FAQ";
import type { FooterCopy } from "@components/bluetooth-landing/Footer";
import type { HeroCopy } from "@components/bluetooth-landing/Hero";
import type { NavbarCopy } from "@components/bluetooth-landing/Navbar";
import type { ProcessCopy } from "@components/bluetooth-landing/Process";
import type { ServicesCopy } from "@components/bluetooth-landing/Services";
import type { TargetAudienceCopy } from "@components/bluetooth-landing/TargetAudience";
import type { ValuePropositionCopy } from "@components/bluetooth-landing/ValueProposition";

export const title =
  "Audyt kodu i modernizacja legacy systemów - ocena ryzyk przed dalszym rozwojem";

export const description =
  "Audyt kodu, architektury i infrastruktury AWS oraz modernizacja legacy systemów — ocena ryzyk przed dalszym rozwojem. Polski software house: raport, plan napraw i opcjonalne wdrożenie zmian.";

export const structuredData = [
  organizationStructuredData(description),
  serviceStructuredData(
    "Audyt kodu i modernizacja legacy systemów",
    description,
    "Code audit and legacy system modernization",
  ),
  faqStructuredData(faqsPl),
];

export const navbarCopy: NavbarCopy = {
  ...navbarCopyPl,
  cta: "Zamów audyt",
};

export const heroCopy: HeroCopy = {
  header: "Audyt kodu",
  headerTail: "i modernizacja legacy",
  description:
    "Ocena ryzyk w kodzie, architekturze i infrastrukturze — zanim zainwestujesz w dalszy rozwój albo przepisanie systemu. Audyt, plan modernizacji i wdrożenie zmian jako kolejne etapy jednej rozmowy, nie trzy osobne projekty.",
  cta: {
    text: "Omów zakres audytu",
    href: "#kontakt",
  },
  secondaryCta: {
    text: "Poznaj proces",
    href: "#proces",
  },
  benefits: [
    { text: "Kod, architektura, cloud", Icon: CheckCircle },
    { text: "Raport ryzyk i plan napraw", Icon: CheckCircle },
    { text: "Opcja modernizacji po audycie", Icon: CheckCircle },
  ],
};

export const targetAudienceCopy: TargetAudienceCopy = {
  header: {
    h2: "Kim są nasi klienci?",
    h3: "Kiedy warto zamówić audyt kodu lub modernizację legacy?",
    p: "Ta ścieżka jest dla CTO, PM i founderów z istniejącym systemem — nie z pomysłem na MVP. Potrzebujecie diagnozy przed kolejnymi pieniędzmi na development, po słabym handouverze albo gdy „mała zmiana” trwa tygodnie. Który scenariusz jest Wasz?",
  },
  points: [
    {
      Icon: AlertTriangle,
      title: "Każda zmiana w produkcie jest droga i ryzykowna",
      description:
        "Legacy blokuje sprinty: brak testów, splątane zależności, strach przed deployem. Audyt pokazuje, co naprawić najpierw, zamiast „przepisać wszystko”.",
    },
    {
      Icon: RefreshCw,
      title: "Przejmujecie kod po innym wykonawcy",
      description:
        "Repo jest, dokumentacji mało. Potrzebujecie niezależnej oceny jakości, długu technicznego i realnego kosztu dalszego rozwoju.",
    },
    {
      Icon: Cloud,
      title: "Podejrzewacie problemy w AWS / infrastrukturze",
      description:
        "Koszty rosną, uprawnienia są niejasne, brak backupów albo monitoring „na oko”. Audyt cloud łączymy z oceną aplikacji — bo ryzyko często leży na styku.",
    },
    {
      Icon: Layers,
      title: "Rozważacie modernizację, ale nie wiecie od czego zacząć",
      description:
        "Refaktor modułu, migracja bazy, wydzielenie serwisów — decyzja wymaga mapy ryzyk i priorytetów, nie wishlisty technologii.",
    },
    {
      Icon: Shield,
      title: "Przed rundą inwestycyjną lub due diligence",
      description:
        "Potrzebujecie ustrukturyzowanego raportu: ryzyka, rekomendacje, szacunek prac — język zrozumiały dla zarządu i inwestora.",
    },
  ],
  cta: {
    h4: "Brzmi znajomo?",
    text: "Opiszcie system i decyzję, którą chcecie podjąć po audycie — zaproponujemy zakres: kod, architektura, infrastruktura albo cały pakiet.",
    href: "#kontakt",
    buttonText: "Umów rozmowę o audycie",
  },
};

export const valuePropositionCopy: ValuePropositionCopy = {
  header: {
    h2: "Nasza specjalizacja",
    h3: "Dlaczego audyt i modernizacja w jednej ofercie?",
    p: "Dawniej klienci dostawali osobne strony na audyt kodu, modernizację legacy i audyt chmury. W praktyce to jedna rozmowa: najpierw ocena ryzyk, potem plan, potem (opcjonalnie) wdrożenie. Tak prowadzimy projekty — bez sztucznego dzielenia diagnozy i leczenia.",
    imageAlt: "Audyt kodu i modernizacja legacy — ocena ryzyk przed rozwojem",
  },
  values: [
    {
      Icon: Target,
      title: "Diagnoza pod decyzję biznesową",
      description:
        "Raport nie jest checklistą stylu kodu. Ma pomóc zdecydować: utrzymanie, punktowy refaktor, modernizacja czy zmiana dostawcy.",
    },
    {
      Icon: Layers,
      title: "Kod + architektura + infrastruktura",
      description:
        "Oceniamy aplikację i środowisko razem — AWS, CI/CD, uprawnienia, koszty — bo awarie rzadko mieszkają tylko w jednej warstwie.",
    },
    {
      Icon: Map,
      title: "Plan modernizacji z priorytetami",
      description:
        "Co zrobić w 30 / 90 dniach, co świadomie odłożyć. Unikamy „big bang rewrite”, gdy wystarczy stabilizacja i wąski refaktor.",
    },
    {
      Icon: Wrench,
      title: "Możemy wdrożyć to, co rekomendujemy",
      description:
        "Po audycie możecie iść sami — albo zostajemy do modernizacji i utrzymania. Continuity zamiast kolejnego handoveru raportu.",
    },
    {
      Icon: Users,
      title: "Rozmowa z inżynierami, nie z warstwą sprzedaży",
      description:
        "Scoping i przegląd robią osoby, które potem mogą poprawiać kod i infrastrukturę.",
    },
  ],
};

export const servicesCopy: ServicesCopy = {
  header: {
    h2: "Usługi",
    h3: "Co obejmuje audyt kodu i modernizacja legacy",
    p: "Od scoringu ryzyk po plan i wdrożenie zmian. Zakres dobieramy do etapu: sam audyt, audyt + roadmapa albo pełna modernizacja wybranych obszarów.",
  },
  services: [
    {
      Icon: FileSearch,
      title: "Audyt kodu i jakości inżynieryjnej",
      description:
        "Czytelność, testy, dług techniczny, bezpieczeństwo zależności, gotowość do dalszego rozwoju i utrzymania.",
    },
    {
      Icon: Layers,
      title: "Audyt architektury",
      description:
        "Granice systemu, integracje, skalowalność, miejsca wysokiego ryzyka przy zmianach produktowych.",
    },
    {
      Icon: Cloud,
      title: "Audyt infrastruktury cloud (np. AWS)",
      description:
        "Konta, sieci, IAM, koszty, backupy, monitoring, pipeline wdrożeń — w kontekście Waszej aplikacji.",
    },
    {
      Icon: FileText,
      title: "Raport ryzyk i rekomendacje",
      description:
        "Priorytety, szacunek prac, warianty: stabilizacja / refaktor / modernizacja — język dla technicznych i zarządu.",
    },
    {
      Icon: Map,
      title: "Plan modernizacji legacy",
      description:
        "Roadmapa etapów, kryteria sukcesu, ryzyka migracji i decyzje „co zostaje, co wymieniamy”.",
    },
    {
      Icon: Code,
      title: "Wdrożenie modernizacji",
      description:
        "Refaktor modułów, migracje, hardening, poprawa CI/CD — bez zatrzymywania całego biznesu na rewrite.",
    },
    {
      Icon: Server,
      title: "Stabilizacja przed większą przebudową",
      description:
        "Gdy system pali się na produkcji — najpierw przewidywalność, potem większe zmiany architektoniczne.",
    },
    {
      Icon: LifeBuoy,
      title: "Handover do utrzymania lub zespołu in-house",
      description:
        "Dokumentacja, runbooki i opcja przejścia na SLA po zakończeniu audytu lub modernizacji.",
    },
  ],
  cta: {
    h4: "Nie wiecie, czy potrzebujecie audytu czy od razu modernizacji?",
    p: "Zacznijcie od rozmowy o symptomach — zwykle najpierw krótki scoping, potem decyzja o zakresie diagnozy.",
    button: {
      text: "Umów rozmowę o audycie",
      href: "#kontakt",
    },
  },
};

export const processCopy: ProcessCopy = {
  header: {
    h2: "Nasz Proces",
    h3: "Jak wygląda audyt i droga do modernizacji",
    p: "Od NDA i dostępu po raport oraz — jeśli zdecydujecie — wdrożenie planu. Kolejne etapy jednej rozmowy z klientem.",
  },
  stepNumber: "Krok",
  steps: [
    {
      Icon: Calendar,
      title: "Rozmowa o systemie i decyzji",
      duration: "w kilka godzin",
      description:
        "Co boli, jaki jest cel audytu (rozwój, inwestor, zmiana dostawcy), jaki dostęp da się zapewnić.",
    },
    {
      Icon: FileText,
      title: "NDA i scoping",
      duration: "w kilka dni",
      description:
        "Zakres: kod / architektura / cloud, lista materiałów, widełki kosztu i termin raportu.",
    },
    {
      Icon: Search,
      title: "Przegląd techniczny",
      duration: "kilka–kilkanaście dni",
      description:
        "Analiza repo, architektury, infrastruktury i procesów wdrożeniowych. Pytania do zespołu w trakcie.",
    },
    {
      Icon: FileSearch,
      title: "Raport ryzyk",
      duration: "podsumowanie",
      description:
        "Findings, priorytety, rekomendacje i szacunek prac — warsztat omówienia z interesariuszami.",
    },
    {
      Icon: Map,
      title: "Plan modernizacji (opcjonalnie)",
      duration: "ustalenie harmonogramu",
      description:
        "Etapy, zależności, kryteria sukcesu i model współpracy na wdrożenie.",
    },
    {
      Icon: Wrench,
      title: "Wdrożenie zmian",
      duration: "wg planu",
      description:
        "Refaktor, migracje, hardening — w iteracjach, z kontrolą ryzyka na produkcji.",
    },
    {
      Icon: CheckCircle,
      title: "Weryfikacja efektów",
      duration: "po etapie",
      description:
        "Metryki: stabilność, czas zmian, koszty infra, gotowość do dalszego rozwoju.",
    },
    {
      Icon: LifeBuoy,
      title: "Utrzymanie lub handover",
      duration: "na stałe / wg potrzeb",
      description:
        "Przejście na SLA albo przekazanie planu i kodu Waszemu zespołowi.",
    },
  ],
  cta: {
    href: "#kontakt",
    text: "Zamów audyt kodu",
  },
};

export const aboutUsCopy = aboutUsCopyPl({
  h3: "Polski software house do audytu i modernizacji legacy",
  p1: "Jesteśmy software house'em z Polski — audytujemy kod, architekturę i infrastrukturę istniejących systemów, a potem (jeśli chcecie) wdrażamy plan modernizacji. Pracujemy z CTO i PM, którzy mają produkt na produkcji, nie sam pomysł.",
  p2: "Kuba prowadzi przeglądy backendu i cloud, Mateusz pomaga ocenić wpływ na UX i release'e, a zespół produktowy spina diagnozę z realnym planem prac — bez warstwy account managerów między Wami a inżynierami.",
  imageAlt: "Zespół Blues Brackets — audyt kodu i modernizacja legacy",
});

export const faqCopy: FAQCopy = {
  header: {
    h2: "FAQ",
    h3: "Najczęściej zadawane pytania o audyt kodu i modernizację",
  },
  faqs: faqsPl,
};

const auditContactForm: FooterCopy["contactForm"] = {
  ...contactFormPl,
  fields: {
    ...contactFormPl.fields,
    topic: {
      label: "Opis systemu / celu audytu",
      placeholder:
        "Krótko opisz stack, czy chodzi o audyt kodu, architektury, AWS, modernizację legacy — oraz jaką decyzję chcecie podjąć po raporcie...",
      requiredError: "Opisz swój projekt",
      maxLengthError: "Opis może mieć maksymalnie 2000 znaków",
    },
  },
};

export const footerCopy: FooterCopy = {
  header: {
    h2: "Chcesz wiedzieć, na czym stoicie przed kolejnym rozwojem?",
    p: "Umów rozmowę — opiszcie system i cel audytu, a zaproponujemy zakres diagnozy i dalsze kroki.",
  },
  cta: {
    href: "#kontakt",
    text: "Umów rozmowę o audycie",
    p: "Audyt kodu, architektury i infrastruktury oraz modernizacja legacy. Polski software house — ocena ryzyk przed dalszym rozwojem.",
    email: "hello@bluesbrackets.com",
    phone: "+48 535 462 678",
  },
  links: {
    h4: "Na skróty",
    links: [
      { href: "#uslugi", text: "Nasze usługi" },
      { href: "#proces", text: "Proces współpracy" },
      { href: "#faq", text: "FAQ" },
      {
        href: "/utrzymanie-i-audyt-oprogramowania",
        text: "Utrzymanie i audyt oprogramowania",
      },
      ...maintenanceClusterLinks.filter(
        l =>
          l.href !== "/utrzymanie-i-audyt-oprogramowania" &&
          l.href !== "/audyt-kodu-i-modernizacja-legacy",
      ),
    ],
  },
  knowledgeBase: {
    h4: "Baza wiedzy",
    links: [...maintenanceKnowledgeBaseLinks],
  },
  copyright: "bluesBrackets. Wszelkie prawa zastrzeżone.",
  privacyPolicy: "Polityka prywatności",
  privacyPolicyHref: "/policy",
  contactForm: auditContactForm,
};
